import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Grid, Card, CardContent, CardMedia, Stack, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SchoolIcon from '@mui/icons-material/School';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PsychologyIcon from '@mui/icons-material/Psychology';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { useNavigate } from 'react-router-dom';
import { useWeeklyCheckIn } from '../../hooks/useWeeklyCheckIn';
import { useAuth } from '../../contexts/AuthContext';
import { courses, images, flows, responses } from '../../services/api';
import { useFlowStatuses } from '../../hooks/useFlowStatuses';
import { useEventTracking } from '../../hooks/useEventTracking';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import editIconSvg from '../../assets/edit-icon.svg';

interface RadarDataPoint {
  category: string;
  value: number;
}

interface RadarChartData {
  initial: RadarDataPoint[];
  latest: RadarDataPoint[];
}

interface Course {
  id: number;
  name: string;
  description: string;
  image_url: string;
  progress: number;
  completed: boolean;
  flow_name: string;
}

interface ProgressCircleProps {
  isMobile?: boolean;
}

const ProgressCircle = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})<ProgressCircleProps>(({ theme, isMobile }) => ({
  position: 'relative',
  width: isMobile ? '180px' : '250px',
  height: isMobile ? '180px' : '250px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: isMobile ? '0 auto' : 'initial',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: -2,
    left: -2,
    right: -2,
    bottom: -2,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, rgba(75, 78, 252, 0.2), rgba(75, 78, 252, 0.1))',
    backdropFilter: 'blur(4px)',
  }
}));

const ProgressItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const ProgressIcon = styled(Box)(({ theme }) => ({
  width: '24px',
  height: '24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  backgroundColor: 'rgba(75, 78, 252, 0.1)',
  color: theme.palette.primary.main,
}));

const CourseCard = styled(Card)(({ theme }) => ({
  backgroundColor: 'rgba(38, 38, 38, 0.6)',
  borderRadius: '25px',
  overflow: 'hidden',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
  },
}));

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  backdropFilter: 'blur(10px)',
}));

const CheckInButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  borderRadius: '25px',
  padding: '12px 24px',
  textTransform: 'none',
  fontSize: '1rem',
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
}));

const DayIndicator = styled(Box)<{ completed?: boolean }>(({ theme, completed }) => ({
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  border: `2px solid ${completed ? theme.palette.primary.main : 'rgba(255, 255, 255, 0.2)'}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: completed ? theme.palette.primary.main : 'rgba(255, 255, 255, 0.5)',
  backgroundColor: completed ? 'rgba(75, 78, 252, 0.1)' : 'transparent',
}));

const Home: React.FC = () => {
  const [progress, setProgress] = useState(0);
  const [coursesInProgress, setCoursesInProgress] = useState<Course[]>([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [courseFlows, setCourseFlows] = useState<any[]>([]);
  const [strengthArea, setStrengthArea] = useState<RadarDataPoint | null>(null);
  const [growthArea, setGrowthArea] = useState<RadarDataPoint | null>(null);
  const navigate = useNavigate();
  const { weeklyStatus, isLoading, error } = useWeeklyCheckIn();
  const { user } = useAuth();
  const daysCompleted = weeklyStatus.filter(Boolean).length;
  const [flowNames, setFlowNames] = useState<string[]>([]);
  const { statuses } = useFlowStatuses(flowNames);
  const { track } = useEventTracking();

  // Add debug logging
  console.log('Auth user data:', user);

  // Track page view when component mounts
  useEffect(() => {
    track('page_view', { page: 'home' });
  }, [track]);

  const getFirstName = () => {
    if (!user?.username) {
      console.log('No username found in user data');
      return 'there';
    }
    
    // First try to get the first name by splitting on common name separators
    const nameParts = user.username.split(/[._-]/);
    console.log('Username parts:', nameParts);
    
    // Capitalize the first letter of the first part
    return nameParts[0].charAt(0).toUpperCase() + nameParts[0].slice(1);
  };

  // Fetch course flows
  useEffect(() => {
    const fetchCourseFlows = async () => {
      try {
        // Focus only on the lifeplan course (ID: 4)
        const courseId = 4;
        const flowsResponse = await courses.getFlows(courseId);
        setCourseFlows(flowsResponse.data || []);
      } catch (error) {
        console.error('Error fetching course flows:', error);
      }
    };

    fetchCourseFlows();
  }, []);

  // Fetch radar chart data
  useEffect(() => {
    const fetchRadarData = async () => {
      try {
        const response = await responses.getRadarChartData();
        const latestData: RadarDataPoint[] = response.data.latest;
        
        if (latestData && latestData.length > 0) {
          // Find highest and lowest values
          let highest: RadarDataPoint = latestData[0];
          let lowest: RadarDataPoint = latestData[0];
          
          latestData.forEach((item: RadarDataPoint) => {
            if (item.value > highest.value) highest = item;
            if (item.value < lowest.value) lowest = item;
          });
          
          setStrengthArea(highest);
          setGrowthArea(lowest);
        }
      } catch (error) {
        console.error('Error fetching radar chart data:', error);
      }
    };
    
    fetchRadarData();
  }, []);

  // Update progress and courses when flow statuses change
  useEffect(() => {
    if (!courseFlows.length || !flowNames.length) return;

    const updateCourseProgress = async () => {
      try {
        console.log('HOMEPAGE: Starting course progress update');
        setLoadingCourses(true);
        const inProgressCourses: Course[] = [];
        
        // Count completed flows
        let completedFlowsCount = 0;
        const totalFlows = courseFlows.length;
        console.log('HOMEPAGE: Total flows found:', totalFlows);
        
        // Find the next incomplete flow
        let nextIncompleteFlow = null;
        
        for (const flowItem of courseFlows) {
          const flowDetails = flowItem.flow_details || {};
          const flowName = flowDetails.name;
          console.log('HOMEPAGE: Processing flow:', { 
            flowName, 
            flowId: flowDetails.id,
            hasDetails: !!flowDetails,
            status: flowName ? statuses[flowName] : 'No status' 
          });
          
          if (flowName && statuses[flowName]) {
            if (statuses[flowName].completionStatus?.completed_at) {
              completedFlowsCount++;
              console.log('HOMEPAGE: Flow completed:', flowName);
            } else if (!nextIncompleteFlow) {
              nextIncompleteFlow = flowItem;
              console.log('HOMEPAGE: Found next incomplete flow:', flowName);
            }
          }
        }
        
        // Calculate progress percentage
        const progressPercentage = totalFlows > 0 ? Math.round((completedFlowsCount / totalFlows) * 100) : 0;
        console.log('HOMEPAGE: Progress calculation:', { completedFlows: completedFlowsCount, total: totalFlows, percentage: progressPercentage });
        setProgress(progressPercentage);
        
        // Add the next incomplete flow to courses in progress
        if (nextIncompleteFlow) {
          const flowDetails = nextIncompleteFlow.flow_details;
          console.log('HOMEPAGE: Processing next incomplete flow:', { 
            flowName: flowDetails?.name,
            flowId: flowDetails?.id,
            hasDetails: !!flowDetails
          });
          
          if (flowDetails?.id) {
            try {
              // Get flow questions using flow ID
              console.log('HOMEPAGE: Fetching questions for flow ID:', flowDetails.id);
              const questionsResponse = await flows.getQuestions(flowDetails.id);
              const questions = questionsResponse.data;
              console.log('HOMEPAGE: Questions received:', {
                count: questions?.length || 0,
                firstQuestion: questions?.[0] ? {
                  id: questions[0].id,
                  hasImage: !!questions[0].visual_component_url,
                  imageUrl: questions[0].visual_component_url
                } : 'No questions'
              });
              
              // Get signed URL for the first question's image
              let imageUrl = '';
              if (questions && questions.length > 0) {
                const firstQuestion = questions[0];
                if (firstQuestion?.visual_component_url) {
                  try {
                    console.log('HOMEPAGE: Getting signed URL for:', firstQuestion.visual_component_url);
                    imageUrl = await images.getSignedUrl(firstQuestion.visual_component_url);
                    console.log('HOMEPAGE: Received signed URL:', imageUrl);
                  } catch (imageError) {
                    console.error('HOMEPAGE: Error fetching image:', imageError);
                  }
                } else {
                  console.log('HOMEPAGE: First question has no image URL');
                }
              } else {
                console.log('HOMEPAGE: No questions found for flow');
              }
              
              const courseData = {
                id: flowDetails.id,
                name: flowDetails.name,
                description: flowDetails.description || '',
                image_url: imageUrl || '/default-course-image.jpg',
                progress: progressPercentage,
                completed: false,
                flow_name: flowDetails.name
              };
              console.log('HOMEPAGE: Adding course to in-progress list:', courseData);
              inProgressCourses.push(courseData);
            } catch (error) {
              console.error('HOMEPAGE: Error fetching flow data:', error);
            }
          }
        } else {
          console.log('HOMEPAGE: No incomplete flows found');
        }
        
        console.log('HOMEPAGE: Setting courses in progress:', inProgressCourses);
        setCoursesInProgress(inProgressCourses);
      } catch (error) {
        console.error('HOMEPAGE: Error updating course progress:', error);
      } finally {
        setLoadingCourses(false);
        console.log('HOMEPAGE: Course loading completed');
      }
    };

    updateCourseProgress();
  }, [courseFlows, flowNames, statuses]);

  useEffect(() => {
    const loadFlowNames = async () => {
      try {
        const flowNamesList: string[] = [];
        
        for (const flowItem of courseFlows) {
          const flowDetails = flowItem.flow_details || {};
          const flowName = flowDetails.name;
          
          if (flowName) {
            flowNamesList.push(flowName);
          }
        }
        
        setFlowNames(flowNamesList);
      } catch (error) {
        console.error('Error loading flow names:', error);
      }
    };

    loadFlowNames();
  }, [courseFlows]);

  const days = ['S', 'M', 'T', 'W', 'Th', 'F', 'Sa'];

  const { isMobile } = useResponsiveLayout();

  return (
    <Box sx={{ 
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      px: isMobile ? 2 : 8,
      pt: isMobile ? 3 : 6,
      pb: isMobile ? 2 : 4
    }}>
      <Box sx={{ 
        width: '100%',
        maxWidth: 'min(100%, 1000px)',
        display: 'flex',
        flexDirection: 'column',
        pl: isMobile ? 1 : 2,
        pr: isMobile ? 1 : 4
      }}>
        <Typography variant={isMobile ? "h5" : "h4"} gutterBottom sx={{ color: 'text.primary', mb: isMobile ? 2 : 4 }}>
          Good morning, {getFirstName()}
        </Typography>

        <Grid container spacing={isMobile ? 4 : 12} alignItems="flex-start"> 
          {/* Left Column - Course Progress */}
          <Grid item xs={12} md={5} sx={{ 
            pl: isMobile ? 0 : 2,
            pr: isMobile ? 0 : 8
          }}>
            <Box sx={{ mb: 6, mt: 0 }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', mb: 3 }}>
                Today's Courses
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" gutterBottom sx={{ mb: 4 }}>
                Continue your progress
              </Typography>

              <Stack spacing={3}>
                {loadingCourses ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  coursesInProgress.map((course, index) => (
                    <Card
                      key={index}
                      onClick={() => {
                        track('button_click', {
                          action: 'course_start',
                          course_name: course.flow_name,
                          course_id: course.id
                        });
                        navigate(`/flows/${course.flow_name}`);
                      }}
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        cursor: 'pointer',
                        transition: 'transform 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'scale(1.02)',
                        },
                      }}
                    >
                      <CardMedia
                        component="img"
                        height="200"
                        image={course.image_url || '/default-course-image.jpg'}
                        alt={course.name}
                        sx={{ objectFit: 'cover' }}
                      />
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Typography gutterBottom variant="h5" component="h2">
                          {course.name}
                        </Typography>
                        {course.progress > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <CircularProgress variant="determinate" value={course.progress} />
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              Progress: {course.progress}%
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  ))
                )}
              </Stack>
            </Box>
          </Grid>

          {/* Right Column - Certification Progress & Insights */}
          <Grid item xs={12} md={7} sx={{ 
            pl: isMobile ? 0 : 8,
            pr: isMobile ? 0 : 2,
            mt: isMobile ? 4 : 0
          }}>
            <Box sx={{ mb: 6, mt: 0 }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', mb: 1 }}>
                My Certification Progress
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
                Behavioral Health Advisor 101
              </Typography>

              <Grid container spacing={isMobile ? 2 : 4} sx={{ mb: 4 }}>
                <Grid item xs={12} md={6} sx={{ 
                  display: 'flex', 
                  justifyContent: isMobile ? 'center' : 'flex-start',
                  mb: isMobile ? 2 : 0
                }}>
                  <ProgressCircle isMobile={isMobile}>
                    <CircularProgress
                      variant="determinate"
                      value={progress}
                      size={isMobile ? 180 : 250}
                      thickness={4}
                      sx={{
                        color: theme => theme.palette.primary.main,
                        position: 'absolute',
                      }}
                    />
                    <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
                      <Typography variant={isMobile ? "h3" : "h2"} sx={{ color: 'text.primary' }}>
                        {progress}%
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        complete
                      </Typography>
                    </Box>
                  </ProgressCircle>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Stack spacing={3} sx={{ height: '100%', justifyContent: 'center' }}>
                    {strengthArea && (
                      <ProgressItem>
                        <ProgressIcon>
                          <TrendingUpIcon fontSize="small" />
                        </ProgressIcon>
                        <Box>
                          <Typography variant="h6" sx={{ color: 'text.primary', display: 'flex', alignItems: 'center', gap: 1 }}>
                            {strengthArea.category}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Strength
                          </Typography>
                        </Box>
                      </ProgressItem>
                    )}
                    {growthArea && (
                      <ProgressItem>
                        <ProgressIcon>
                          <PsychologyIcon fontSize="small" />
                        </ProgressIcon>
                        <Box>
                          <Typography variant="h6" sx={{ color: 'text.primary', display: 'flex', alignItems: 'center', gap: 1 }}>
                            {growthArea.category}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Growth Opportunity
                          </Typography>
                        </Box>
                      </ProgressItem>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </Box>

            {/* Insights Section */}
            <Box sx={{ mb: 6 }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>
                Unlock this Week's Insights
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CheckInButton
                    onClick={async () => {
                      try {
                        // Track daily check-in button click
                        track('button_click', {
                          action: 'start_daily_checkin',
                          flow_name: 'daily_checkin_v0'
                        });
                        
                        const flowResponse = await flows.getFlow('daily_checkin_v0');
                        const flow = flowResponse.data;
                        
                        if (!flow.has_preferences) {
                          navigate('/flows/daily_checkin_v0/customize');
                        }
                        
                        await flows.resetFlow('daily_checkin_v0');
                        navigate('/flows/daily_checkin_v0');
                      } catch (error) {
                        console.error('Failed to check daily checkin flow:', error);
                      }
                    }}
                  >
                    Fill out today's daily scores
                  </CheckInButton>
                  <IconButton
                    aria-label="Customize daily check-in"
                    sx={{ ml: 1, bgcolor: 'rgba(255,255,255,0.08)', borderRadius: '10px' }}
                    onClick={() => {
                      track('button_click', {
                        action: 'customize_daily_checkin'
                      });
                      navigate('/daily-checkin-customization');
                    }}
                  >
                    <Box component="img" src={editIconSvg} alt="Edit" sx={{ width: 24, height: 24 }} />
                  </IconButton>
                </Box>
              </Box>

              <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                {isLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : error ? (
                  <Typography color="error" variant="body2">
                    {error}
                  </Typography>
                ) : (
                  days.map((day, index) => (
                    <DayIndicator key={day} completed={weeklyStatus[index]}>
                      {weeklyStatus[index] ? <CheckCircleIcon /> : day}
                    </DayIndicator>
                  ))
                )}
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default Home;